import * as React from 'react';
import { Check } from '@ankr.com/ui';
import { Box, PopoverProps, Typography } from '@mui/material';
import Popover from '@mui/material/Popover';
import { makeStyles } from 'tss-react/mui';

import { useChainList } from 'modules/common';
import { useGetChainIdQuery, useSwitchNetworkMutation } from 'modules/store';

interface Props extends PopoverProps {
  className?: string;
}

export function NetworkMenu({ className, ...rest }: Props): JSX.Element {
  const { classes, cx } = useStyles();
  const { data: currentChainId } = useGetChainIdQuery();
  const chains = useChainList();
  const [switchNetwork] = useSwitchNetworkMutation();
  const { onClose } = rest;

  return (
    <Popover
      data-tid="layout-header-account-network-menu"
      classes={{ paper: cx(classes.container, className) }}
      {...rest}
    >
      {chains.map(({ chainId, icon, name }) => {
        const isCurrentChain = chainId === currentChainId;

        return (
          <div
            data-tid={`layout-header-account-network-menu-chain-${chainId}`}
            key={chainId}
            className={cx(classes.network, isCurrentChain && classes.disabled)}
            onClick={async event => {
              await switchNetwork({ chainId });
              if (onClose) {
                onClose(event, 'backdropClick');
              }
            }}
          >
            {icon.default && (
              <Box component="img" src={icon.default} alt={name} width={24} />
            )}

            <Typography
              variant="body4"
              sx={theme => ({
                ml: 2,
                flex: 1,
                color: isCurrentChain
                  ? theme.palette.primary.main
                  : theme.palette.grey[900],
              })}
            >
              {name}
            </Typography>

            <Check
              sx={theme => ({
                ml: 2,
                color: theme.palette.primary.main,
                visibility: isCurrentChain ? 'initial' : 'hidden',
              })}
            />
          </div>
        );
      })}
    </Popover>
  );
}

const useStyles = makeStyles()(theme => ({
  container: {
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
  },
  disabled: {
    pointerEvents: 'none',
  },
  network: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
  },
}));
